import {
	GROUP,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName
} from 'consts';
import { camelCase } from 'lodash';
import { IUserAttribute } from 'types/api';
import { isValidCertificate, URL_MATCH } from 'utils/string';
import { z } from 'zod';

// Avoid Zod's eval capability probe under the app's strict CSP.
z.config({ jitless: true });

export const getSchemaShape = (
	zSchema: z.ZodObject<any>
): Record<string, z.ZodType> => zSchema.shape;

const stringValidation = (required: boolean) =>
	required
		? z
				.string({ error: 'ssoConfig.fields.validation.required' })
				.nonempty('ssoConfig.fields.validation.required')
		: z.string().nullable().optional();

const urlValidation = (required: boolean) =>
	required
		? z
				.string({ error: 'ssoConfig.fields.validation.required' })
				.regex(URL_MATCH, 'ssoConfig.fields.validation.url')
				.nonempty('ssoConfig.fields.validation.required')
		: z
				.union([
					z.string().regex(URL_MATCH, 'ssoConfig.fields.validation.url'),
					z.literal('')
				])
				.nullable()
				.optional();

const certificateValidation = (required: boolean) =>
	required
		? z
				.string({ error: 'ssoConfig.fields.validation.required' })
				.nonempty('ssoConfig.fields.validation.required')
				.refine(isValidCertificate, 'ssoConfig.fields.validation.certificate')
		: z
				.union([
					z
						.string()
						.refine(
							isValidCertificate,
							'ssoConfig.fields.validation.certificate'
						),
					z.literal('')
				])
				.nullable()
				.optional();

export const commonFieldsValidation = (
	mandatoryUserAttributes: string[] = []
) => {
	const isSSODomainsRequired = mandatoryUserAttributes.includes(
		camelCase(IdPCommonInputFieldName.SSODomains)
	);

	return z.object({
		[IdPCommonInputFieldName.SSODomains]: isSSODomainsRequired
			? z.array(z.string()).min(1, 'ssoConfig.fields.validation.ssoDomains')
			: z.array(z.string()).optional()
	});
};

const userAttributeMappingItemSchema = z.object({
	idpAttr: z.string().nullable(),
	spAttr: z.string().nullable()
});

export const createUserAttributeValidation = (
	userAttributes: IUserAttribute[]
) =>
	z.object({
		[IdPCommonInputFieldName.UserAttributes]: z
			.array(userAttributeMappingItemSchema)
			.nullable()
			.superRefine((items, ctx) => {
				// Note we can add issues to specific items, this required a change in the way errors are handled in SSOConfigFieldsContext.tsx
				if (!items) return;

				const missingLabels: string[] = userAttributes
					.filter(
						(attr) =>
							attr.required &&
							!items.some(
								(item) => item.spAttr === attr.value && !!item.idpAttr
							)
					)
					.map((attr) => attr.label);

				if (missingLabels.length > 0) {
					ctx.addIssue({
						code: 'custom',
						message: 'ssoConfig.fields.mapping.validation.missingMappings',
						params: { missingAttributes: missingLabels.join(', ') }
					});
				}
			})
	});

export const createGroupsAttributeValidation = (
	mandatoryUserAttributes: string[] = []
) => {
	const isRequired = mandatoryUserAttributes.includes(GROUP);
	return z.object({
		[IdPCommonInputFieldName.GroupsAttribute]: userAttributeMappingItemSchema
			.nullable()
			.default(null)
			.superRefine((val, ctx) => {
				if (isRequired && !val?.idpAttr) {
					ctx.addIssue({
						code: 'custom',
						message: 'ssoConfig.validation.groupsAttributeRequired'
					});
				}
			})
	});
};

export const samlValidationLaxSchema = (
	mandatoryUserAttributes: string[] = [],
	userAttributes: IUserAttribute[] = [],
	validateCertificate = true
) =>
	commonFieldsValidation(mandatoryUserAttributes)
		.merge(createUserAttributeValidation(userAttributes))
		.merge(createGroupsAttributeValidation(mandatoryUserAttributes))
		.extend({
			[IdPSamlInputFieldName.IdPMetadataUrl]: urlValidation(false),
			[IdPSamlInputFieldName.IdPSsoUrl]: urlValidation(false),
			[IdPSamlInputFieldName.IdPIssuer]: stringValidation(false),
			[IdPSamlInputFieldName.IdPCertificate]: validateCertificate
				? certificateValidation(false)
				: stringValidation(false)
		});

export const samlValidationStrictSchema = (
	mandatoryUserAttributes: string[] = [],
	userAttributes: IUserAttribute[] = [],
	validateCertificate = true
) =>
	commonFieldsValidation(mandatoryUserAttributes)
		.merge(createUserAttributeValidation(userAttributes))
		.merge(createGroupsAttributeValidation(mandatoryUserAttributes))
		.extend({
			[IdPSamlInputFieldName.IdPMetadataUrl]: urlValidation(true),
			[IdPSamlInputFieldName.IdPSsoUrl]: urlValidation(true),
			[IdPSamlInputFieldName.IdPIssuer]: stringValidation(true),
			[IdPSamlInputFieldName.IdPCertificate]: validateCertificate
				? certificateValidation(true)
				: stringValidation(true)
		});

export const samlValidationDualSchema = (
	mandatoryUserAttributes: string[] = [],
	userAttributes: IUserAttribute[] = [],
	validateCertificate = true
) =>
	samlValidationLaxSchema(
		mandatoryUserAttributes,
		userAttributes,
		validateCertificate
	).superRefine((fields, zCtx) => {
		if (
			!fields[IdPSamlInputFieldName.IdPMetadataUrl] &&
			!(
				fields[IdPSamlInputFieldName.IdPCertificate] &&
				fields[IdPSamlInputFieldName.IdPSsoUrl] &&
				fields[IdPSamlInputFieldName.IdPIssuer]
			)
		) {
			[
				IdPSamlInputFieldName.IdPMetadataUrl,
				IdPSamlInputFieldName.IdPCertificate,
				IdPSamlInputFieldName.IdPSsoUrl,
				IdPSamlInputFieldName.IdPIssuer
			].forEach((fieldName) => {
				if (!fields[fieldName]) {
					zCtx.addIssue({
						code: 'custom',
						path: [fieldName],
						params: {
							samlMetadataValidation: true
						}
					});
				}
			});
		}
	});

export const samlValidationSingleSchema = (
	mandatoryUserAttributes: string[] = [],
	userAttributes: IUserAttribute[] = [],
	validateCertificate = true
) =>
	samlValidationLaxSchema(
		mandatoryUserAttributes,
		userAttributes,
		validateCertificate
	).superRefine((fields, zCtx) => {
		if (
			!fields[IdPSamlInputFieldName.IdPMetadataUrl] &&
			!(
				fields[IdPSamlInputFieldName.IdPCertificate] &&
				fields[IdPSamlInputFieldName.IdPSsoUrl] &&
				fields[IdPSamlInputFieldName.IdPIssuer]
			)
		) {
			[
				IdPSamlInputFieldName.IdPMetadataUrl,
				IdPSamlInputFieldName.IdPCertificate,
				IdPSamlInputFieldName.IdPSsoUrl,
				IdPSamlInputFieldName.IdPIssuer
			].forEach((fieldName) => {
				if (!fields[fieldName]) {
					zCtx.addIssue({
						code: 'too_small',
						inclusive: true,
						minimum: 0,
						origin: 'string',
						message: 'ssoConfig.fields.validation.required',
						path: [fieldName]
					});
				}
			});
		}
	});

export const oidcValidationSchema = (
	mandatoryUserAttributes: string[] = [],
	userAttributes: IUserAttribute[] = []
) =>
	commonFieldsValidation(mandatoryUserAttributes)
		.merge(createUserAttributeValidation(userAttributes))
		.merge(createGroupsAttributeValidation(mandatoryUserAttributes))
		.extend({
			[IdPOidcInputFieldName.ProviderName]: stringValidation(true),
			[IdPOidcInputFieldName.ClientId]: stringValidation(true),
			[IdPOidcInputFieldName.ClientSecret]: stringValidation(true),
			[IdPOidcInputFieldName.Scopes]: z.array(z.string()).optional(),
			[IdPOidcInputFieldName.GrantType]: stringValidation(true),
			[IdPOidcInputFieldName.Issuer]: urlValidation(false),
			[IdPOidcInputFieldName.AuthorizationEndpoint]: urlValidation(true),
			[IdPOidcInputFieldName.TokenEndpoint]: urlValidation(true),
			[IdPOidcInputFieldName.UserInfoEndpoint]: urlValidation(true),
			[IdPOidcInputFieldName.JWKsEndpoint]: urlValidation(false),
			[IdPOidcInputFieldName.Prompt]: z.array(z.string()).optional(),
			[IdPOidcInputFieldName.UsePkce]: z.boolean().nullable().optional()
		});

// Cross-App Access (XAA / ID-JAG) suite guide configures a single trusted issuer
// end-to-end: the transport fields (issuer + JWKS URI are the minimum required to
// verify the ID-JAG assertion) plus the attribute/group mapping onto it. Mirror the
// OIDC schema shape (common + attribute-mapping + groups) and add the XAA transport
// fields. externalIdClaim defaults to 'sub' and userInfoUri is optional.
export const xaaValidationSchema = (
	userAttributes: IUserAttribute[] = [],
	// The keys of the currently persisted trusted issuers (jwtBearerSettings.issuers). The first is the
	// edited issuer that the form loaded; the rest are other issuers the config manages. Used to reject a
	// rename onto a DIFFERENT existing issuer as a field error instead of throwing at save time.
	existingIssuerKeys: string[] = []
) =>
	// XAA takes NO mandatoryUserAttributes - hence no argument to commonFieldsValidation
	// (ssoDomains) or createGroupsAttributeValidation (groups). Cross-App Access is a
	// machine-to-machine token exchange: the ID-JAG assertion carries whatever the issuing app
	// puts in it, and real IdPs do not send the profile the SSO mandatory list assumes. XAA has
	// no mandatory list of its own either, so useMandatoryUserAttributes returns [] for it and
	// nothing here can be required. It also has no SSO-domains field at all: no XAA guide step
	// declares one and queryXaaToXaaFields never loads one, so requiring ssoDomains failed every
	// save on a permanently-[] field the admin cannot see, silently withholding `save`
	// (useSaveSSOConfigFields) with nothing rendered to explain it. The Login ID (externalID) row
	// stays required through userAttributes - it is method-independent (isMandatoryAttribute).
	commonFieldsValidation()
		.merge(createUserAttributeValidation(userAttributes))
		.merge(createGroupsAttributeValidation())
		.extend({
			[IdPXaaInputFieldName.Issuer]: urlValidation(true),
			[IdPXaaInputFieldName.JwksUri]: urlValidation(false),
			[IdPXaaInputFieldName.SignAlgorithm]: stringValidation(true),
			[IdPXaaInputFieldName.UserInfoUri]: urlValidation(false),
			[IdPXaaInputFieldName.Jit]: stringValidation(false)
		})
		.superRefine((fields, ctx) => {
			// Renaming the edited issuer's URL onto a DIFFERENT existing issuer would collapse two issuers
			// into one on save; surface it as a field error on the Issuer input (centralized error handling)
			// rather than throwing from updateQueryXaaWithFields and catching it with a bespoke toast.
			const issuer = (fields as Record<string, unknown>)[
				IdPXaaInputFieldName.Issuer
			];
			const [editedKey] = existingIssuerKeys;
			if (
				typeof issuer === 'string' &&
				issuer !== editedKey &&
				existingIssuerKeys.includes(issuer)
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [IdPXaaInputFieldName.Issuer],
					message: 'xaaConfig.save.issuerExistsError'
				});
			}
		});

// SCIM guides configure the shared mapping without an SSO connection, so only the parts of the
// mandatory-attribute list a SCIM admin can actually satisfy are enforced. Standard attributes
// arrive in the SCIM payload itself and the guide edits custom-attribute rows only, so the required
// set is narrowed to custom attributes; `ssoDomains` and the `group` assertion attribute describe
// an SSO login the SCIM surfaces never see - requiring either would fail a save on a field the
// admin cannot see or fill (same reasoning as the XAA schema above).
export const scimValidationSchema = (userAttributes: IUserAttribute[] = []) =>
	commonFieldsValidation().merge(
		createUserAttributeValidation(
			userAttributes.filter((userAttribute) => userAttribute.custom)
		)
	);
